// ─── A lista de ROMANEIOS do comprador (Mercado · Romaneios v2) ──────────────
//
// O romaneio é a unidade do negócio do corretor: um produtor, um dia, uma
// carrada. A tela de Romaneios é a lista de TUDO o que ele comprou — venha pelo
// Rizoma (o produtor emitiu e compartilhou) ou do bloco de papel dele.
//
// ⚠️ DUAS FONTES, UMA LISTA, e a `ref` é o que as reconcilia:
//
//     "c:<compartilhamentoId>"  — romaneio do Rizoma (natureza 1)
//     "r:<itemId>"              — registro próprio   (natureza 2)
//
// O prefixo não é enfeite de URL: as duas fontes moram em tabelas diferentes,
// com ids que podem colidir, e a tela precisa de UMA chave estável para abrir o
// detalhe, marcar a seleção e montar a carga. Sem prefixo, `/romaneios/:id`
// teria de adivinhar em qual tabela procurar — e adivinharia errado no dia em
// que dois UUIDs coincidissem.
//
// ⚠️ SEM drizzle aqui. Este arquivo é importado pelo client; `schema.ts` arrasta
// o ORM inteiro junto e estourou o bundle antes.

import { somarDias } from "./mercado-inicio.js";

// ─── A referência estável ────────────────────────────────────────────────────

export const PREFIXO_RIZOMA = "c";
export const PREFIXO_REGISTRO = "r";

export type OrigemRomaneio = "RIZOMA" | "REGISTRO_PROPRIO";

export function refDoRomaneio(origem: OrigemRomaneio, id: string): string {
  return `${origem === "RIZOMA" ? PREFIXO_RIZOMA : PREFIXO_REGISTRO}:${id}`;
}

/**
 * Parte a `ref` em origem + id. `null` para qualquer coisa que não seja uma ref
 * bem formada — link velho e id colado à mão caem no 404 da rota, não numa
 * consulta com id vazio.
 */
export function lerRef(ref: string): { origem: OrigemRomaneio; id: string } | null {
  const bruto = String(ref ?? "").trim();
  const sep = bruto.indexOf(":");
  if (sep <= 0) return null;
  const prefixo = bruto.substring(0, sep);
  const id = bruto.substring(sep + 1).trim();
  if (!id) return null;
  if (prefixo === PREFIXO_RIZOMA) return { origem: "RIZOMA", id };
  if (prefixo === PREFIXO_REGISTRO) return { origem: "REGISTRO_PROPRIO", id };
  return null;
}

/**
 * O `ref` COMO ELE VEM DA ROTA — e não como está no `history`.
 *
 * ⚠️ O wouter lê `location.pathname` CRU (`currentPathname = () => location.pathname`)
 * e não decodifica nada antes de casar a rota. Como o href da lista é montado com
 * `encodeURIComponent`, os dois-pontos chegam ao componente como `%3A`:
 *
 *   href      /mercado/romaneios/c%3A835f…
 *   params    "c%3A835f…"          ← ainda escapado
 *   fetch     c%253A835f…          ← `encodeURIComponent` por cima do que já era
 *   servidor  "c%3A835f…"          ← uma decodificação só, sobra o %3A
 *   schema    recusa → 400 → "Romaneio não encontrado."
 *
 * Foi esse o branco na tela do detalhe em produção (22/09): a rota `/editar` já
 * decodificava, a do detalhe não, e a diferença passou despercebida porque nada
 * aparecia na lista para clicar.
 *
 * Decodificar na LEITURA, e não parar de escapar no href, porque só assim as duas
 * formas funcionam: o link novo (`c%3A…`) e o endereço que alguém digitou ou
 * guardou nos favoritos com os dois-pontos crus (`c:…`, que é caractere legal em
 * path e atravessa `decodeURIComponent` intacto).
 *
 * ⚠️ `try/catch` porque `decodeURIComponent` LANÇA em porcentagem solta
 * (`decodeURIComponent("%")` → URIError). Um link truncado numa conversa de
 * WhatsApp derrubaria a tela inteira em vez de dar "não encontrado".
 */
export function refDaRota(bruto: string | null | undefined): string | undefined {
  if (!bruto) return undefined;
  try {
    return decodeURIComponent(bruto);
  } catch {
    // Não é "engolir erro": a URL malformada É a resposta, e quem chama trata o
    // ref inválido como inexistente — que é o que ele é.
    return bruto;
  }
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

/**
 * Os presets da lista. `mes` é o padrão — o corretor pensa em mês civil, que é
 * como ele fecha com os produtores e como a praça conversa.
 */
/**
 * ⚠️ O RECORTE É PELA DATA DO DOCUMENTO — a da carga (`LinhaRomaneioDTO.data`:
 * a da venda no romaneio do Rizoma, a do carregamento no registro próprio) —,
 * NUNCA pelo vencimento. "Mês passado" é o que foi CARREGADO em agosto, que é
 * como o corretor fecha o mês com cada produtor. Dinheiro por data de
 * vencimento/pagamento é a visão de "A pagar", e só dela.
 *
 * `mes-passado` segue a chave da convenção do repo (CLAUDE.md, "periodo");
 * `mes`, `7d` e `30d` são anteriores a ela e ficam como estão — renomear
 * quebraria link salvo.
 */
export const PERIODOS_ROMANEIOS = ["mes", "mes-passado", "7d", "30d", "tudo", "personalizado"] as const;
export type PeriodoRomaneios = typeof PERIODOS_ROMANEIOS[number];

export const ORIGENS_ROMANEIOS = ["todas", "RIZOMA", "REGISTRO_PROPRIO"] as const;

/**
 * As situações são as PERGUNTAS que ele faz, não estados do banco: o que falta
 * conferir, o que ainda não entrou em caminhão, o que ainda devo, o que está sem
 * prazo combinado. Por isso são chips com contagem, e não um select de status.
 */
export const SITUACOES_ROMANEIOS = [
  "todas",
  "A_CONFERIR",
  "SEM_CARGA",
  "PAGAMENTO_ABERTO",
  "SEM_VENCIMENTO",
] as const;
export type SituacaoRomaneio = typeof SITUACOES_ROMANEIOS[number];

export const SITUACAO_LABEL: Record<SituacaoRomaneio, string> = {
  todas: "Todos",
  A_CONFERIR: "A conferir",
  SEM_CARGA: "Sem carga",
  PAGAMENTO_ABERTO: "Pagamento em aberto",
  SEM_VENCIMENTO: "Sem vencimento",
};

export interface FiltroRomaneios {
  periodo?: PeriodoRomaneios;
  /** Só com `periodo: "personalizado"`. ISO "AAAA-MM-DD". */
  de?: string;
  ate?: string;
  produtorContatoId?: string;
  origem?: OrigemRomaneio;
  situacao?: Exclude<SituacaoRomaneio, "todas">;
  /** Nome, apelido, telefone, número do romaneio ou nº do bloco. */
  q?: string;
}

/**
 * A janela [de, ate] do preset, em dia civil da fazenda.
 *
 * PURA e em `shared/` porque o rótulo do tile ("Romaneios · setembro") e o
 * recorte dos dados têm de concordar: dois cálculos de "este mês" divergiriam no
 * dia 1º, que é exatamente quando alguém olha.
 *
 * `tudo` devolve `null`, e `null` significa SEM JANELA — não uma janela vazia.
 */
export function janelaDoPeriodo(
  periodo: PeriodoRomaneios,
  hoje: string,
  personalizado?: { de?: string; ate?: string },
): { de: string; ate: string } | null {
  if (periodo === "tudo") return null;
  if (periodo === "personalizado") {
    const de = personalizado?.de || "";
    const ate = personalizado?.ate || "";
    // Período personalizado sem nenhuma das duas pontas não é um recorte: cai em
    // "tudo" em vez de devolver uma janela vazia que esconderia a lista inteira.
    if (!de && !ate) return null;
    return { de: de || "0000-01-01", ate: ate || "9999-12-31" };
  }
  if (periodo === "7d") return { de: somarDias(hoje, -6), ate: hoje };
  if (periodo === "30d") return { de: somarDias(hoje, -29), ate: hoje };
  // "mes-passado" — o mês civil ANTERIOR, do dia 1 ao último dia. O último dia
  // é "dia 1 deste mês − 1": nada de tabela de 28/30/31 nem caso de bissexto.
  if (periodo === "mes-passado") {
    const primeiroDesteMes = `${hoje.slice(0, 7)}-01`;
    const ultimo = somarDias(primeiroDesteMes, -1);
    return { de: `${ultimo.slice(0, 7)}-01`, ate: ultimo };
  }
  // "mes" — mês civil corrente, do dia 1 a hoje. O fim é HOJE e não o fim do mês
  // porque romaneio com data futura não existe.
  return { de: `${hoje.slice(0, 7)}-01`, ate: hoje };
}

/** "setembro", "últimos 7 dias" — o rótulo do tile, do MESMO lugar que a janela. */
const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

export function rotuloDoPeriodo(periodo: PeriodoRomaneios, hoje: string): string {
  if (periodo === "tudo") return "tudo";
  if (periodo === "personalizado") return "o período";
  if (periodo === "7d") return "últimos 7 dias";
  if (periodo === "30d") return "últimos 30 dias";
  if (periodo === "mes-passado") {
    // Janeiro olha para dezembro: o mês anterior sai da JANELA, e não de
    // "mês de hoje − 1", que daria o índice −1.
    const janela = janelaDoPeriodo(periodo, hoje);
    return janela ? (MESES[parseInt(janela.de.slice(5, 7), 10) - 1] ?? "o mês passado") : "o mês passado";
  }
  return MESES[parseInt(hoje.slice(5, 7), 10) - 1] ?? "o mês";
}

// ─── A linha ─────────────────────────────────────────────────────────────────

/** Uma classificação dentro do romaneio, com o preço combinado e o valor. */
export interface ItemDoRomaneioDTO {
  /** "Prata 1ª", "Nanica" — já rotulado pelo servidor. */
  classificacao: string;
  variedade: string | null;
  qualidade: string | null;
  kg: number;
  precoKg: number;
  valor: number;
}

/**
 * O estado da conferência do COMPRADOR sobre um romaneio do Rizoma.
 *
 * `null` no registro próprio, e não "CONFERIDO": não existe conferência a fazer
 * sobre o que ele mesmo anotou, e um chip verde ali afirmaria um ato que ninguém
 * praticou.
 */
export type ConferenciaRomaneio = "A_CONFERIR" | "CONFERIDO" | "AJUSTADO";

export interface LinhaRomaneioDTO {
  ref: string;
  origem: OrigemRomaneio;
  /** "042/2026" no Rizoma; o nº do bloco (ou "registro") no registro próprio. */
  numero: string;
  /** A DATA DO DOCUMENTO — a da venda, ou a do carregamento no registro próprio. */
  data: string;
  produtor: {
    contatoId: string | null;
    nome: string;
    /** Fazenda (razão social) ou cidade — o que houver. `null` quando não há. */
    fazenda: string | null;
    noRizoma: boolean;
    telefone: string | null;
  };

  itens: ItemDoRomaneioDTO[];
  /** Os kg que VALEM: conferidos quando há conferência, senão os do documento. */
  kgBase: number;
  /** Os kg como o documento os traz. */
  kgEmitido: number;
  /** `kgBase − kgEmitido` quando a conferência mudou os kg. `null` sem ajuste. */
  ajusteKg: number | null;
  valor: number;
  vencimento: string | null;
  conferencia: ConferenciaRomaneio | null;
  carga: { id: string; identificador: string; status: string } | null;
  /** O status do dinheiro, do escritor único (`aplicarStatusPagamento`). */
  pagamento: string;
}

// ─── Os números do topo ──────────────────────────────────────────────────────

export interface KpisRomaneios {
  romaneios: { n: number; viaRizoma: number; registroProprio: number };
  volume: {
    kg: number;
    /** Uma entrada por variedade presente, maior primeiro — a barra do tile. */
    porVariedade: { variedade: string; kg: number }[];
  };
  valor: { total: number; mediaKg: number | null };
  /**
   * ⚠️ PENDENTES IGNORA O PERÍODO, e é a única coisa nesta tela que ignora.
   * Pendência antiga continua pendência: um romaneio de agosto sem conferir não
   * deixa de precisar de conferência porque a tela está mostrando setembro — e
   * some-lo do contador seria esconder justamente o que já foi esquecido uma vez.
   */
  pendentes: {
    /**
     * Romaneios DISTINTOS que estão a conferir OU sem carga. Não é a soma dos
     * dois abaixo: o romaneio que chegou ontem costuma ser as duas coisas, e
     * "6 a conferir + 12 sem carga = 18 romaneios" contava o mesmo duas vezes
     * (print do Hugo, 24/09).
     */
    n: number;
    aConferir: number;
    semCarga: { n: number; kg: number };
  };
}

export interface TotaisRomaneios {
  n: number;
  nProdutores: number;
  kg: number;
  valor: number;
}

export interface RomaneiosDTO {
  kpis: KpisRomaneios;
  /** Quantos romaneios em cada chip, com período/produtor/origem já aplicados. */
  contagens: Record<SituacaoRomaneio, number>;
  linhas: LinhaRomaneioDTO[];
  totais: TotaisRomaneios;
}

// ─── O detalhe ───────────────────────────────────────────────────────────────

export interface EventoDoRomaneioDTO {
  tipo: string;
  /** Frase pronta — a tela não traduz código de evento. */
  texto: string;
  origem: string;
  em: string;
}

/**
 * O que a tela pode OFERECER neste romaneio. Vem do servidor porque as regras
 * são de domínio (carga fechada trava a edição, pagamento lançado trava a
 * exclusão) e uma segunda cópia delas no React começaria a discordar no primeiro
 * estado novo.
 *
 * ⚠️ É o que a tela MOSTRA, não o que ela pode. A recusa de verdade é do
 * servidor, em `requireCap` e nas travas de cada função.
 */
export interface AcoesDoRomaneioDTO {
  conferir: boolean;
  editar: boolean;
  excluir: boolean;
  incluirEmCarga: boolean;
  enviarAoProdutor: boolean;
}

export interface DetalheRomaneioDTO extends LinhaRomaneioDTO {
  /** Cidade do produtor — fecha a identidade de quem entregou, com nome e fazenda. */
  produtorCidade: string | null;
  /**
   * "Prata 1ª 82,8% · Prata 2ª 17,2%" — a mesma linha do documento do produtor,
   * pela MESMA função (`distribuicaoPorVariedade`). Vazia quando cada variedade
   * tem uma classificação só: aí a linha não diz nada e não aparece.
   *
   * ⚠️ No DETALHE e não na linha da lista: é derivada por item, e cinquenta
   * linhas pagariam a conta para mostrar o que só cabe no painel aberto.
   */
  distribuicao: { rotulo: string; pct: number }[];
  /**
   * O DINHEIRO deste romaneio, para o painel poder oferecer "Informar pagamento".
   *
   * ⚠️ Aqui e não em `LinhaRomaneioDTO`: sai de `calcularStatusPagamento`, que
   * LÊ OS LANÇAMENTOS — uma consulta por romaneio. Na lista seriam N consultas
   * para preencher uma coluna que a lista não mostra; o painel abre um de cada
   * vez, e é nele que o saldo é a diferença entre um toque e abrir `/pagar`.
   *
   * `pago` é o que baixou o saldo de verdade (confirmado e não contestado). Não
   * há mais um `aberto`: com "quem registra quita" o lançamento nasce confirmado,
   * e o informado-esperando-confirmação deixou de existir. A BASE da dívida
   * também não entra aqui: é o `valor` da linha, que já está no DTO.
   */
  dinheiro: { pago: number; saldo: number };
  conferenciaObs: string | null;
  /** O `/p/` do registro próprio. `null` no romaneio do Rizoma — ele tem o `/v/`. */
  token: string | null;
  historico: EventoDoRomaneioDTO[];
  acoes: AcoesDoRomaneioDTO;
}

// ─── Entradas ────────────────────────────────────────────────────────────────

export interface NovoRomaneio {
  produtor: { contatoId?: string; nome?: string; telefone?: string | null; cidade?: string | null; criarNovo?: boolean };
  dataDocumento: string;
  numeroBloco?: string | null;
  itens: { variedade: string; qualidade: string | null; kg: number; precoKg: number }[];
  vencimento?: string | null;
  /** Já entra numa carga EM_FORMACAO. Opcional — o caminho normal é decidir depois. */
  cargaId?: string | null;
}

export interface PatchRomaneio {
  dataDocumento?: string;
  numeroBloco?: string | null;
  itens?: { variedade: string; qualidade: string | null; kg: number; precoKg: number }[];
  vencimento?: string | null;
}

// ─── Códigos de erro (a tela reage a eles, não ao texto) ─────────────────────

export const ERRO_ROMANEIO = {
  /** A ref não existe, ou é de outra org. Sempre 404 — nunca 403. */
  NAO_ENCONTRADO: "NAO_ENCONTRADO",
  /** Só o registro próprio se edita/exclui: o dono do romaneio do Rizoma é o produtor. */
  NAO_EDITAVEL: "ROMANEIO_NAO_EDITAVEL",
  /** A carga dele está FECHADA/ENTREGUE. O corpo traz `identificador`. */
  CARGA_FECHADA: "CARGA_FECHADA",
  /** Já está em outra carga. O corpo traz `identificador`. */
  EM_CARGA: "ROMANEIO_EM_CARGA",
  /** Tem lançamento de pagamento — excluir apagaria dinheiro já movimentado. */
  COM_PAGAMENTO: "ROMANEIO_COM_PAGAMENTO",
  /** Há contato parecido na carteira. O corpo traz `sugestao`. */
  PRODUTOR_PARECIDO: "PRODUTOR_PARECIDO",
  DADOS_INVALIDOS: "DADOS_INVALIDOS",
} as const;

// ─── Distribuição por classificação ──────────────────────────────────────────

/**
 * "Prata 1ª 82,8% · Prata 2ª 17,2%" — a REGRA, num lugar só.
 *
 * ⚠️ A % é RELATIVA AO TOTAL DA MESMA VARIEDADE, nunca ao total do romaneio.
 * Prata e Nanica são produtos diferentes e disputam preços diferentes; misturar
 * as duas num denominador só produziria um número que não responde a pergunta
 * nenhuma. É a mesma convenção de agrupamento do rateio por setor
 * (`server/storage/vendas.ts::getRateioVenda`).
 *
 * ⚠️ VARIEDADE COM UMA CLASSIFICAÇÃO SÓ FICA DE FORA: ela é sempre 100%, e
 * "Prata 1ª 100%" ocupa uma linha para não dizer nada. Por isso o resultado pode
 * vir vazio — e vir vazio é resposta, não falta de dado.
 *
 * ⚠️ A ORDEM É A DA ENTRADA, e não a dos grupos: o documento do produtor lista
 * na ordem em que os itens foram lançados, e a linha de distribuição tem de ler
 * na mesma ordem da tabela logo acima dela.
 *
 * Genérica nos acessores porque as duas pontas guardam o item de formas
 * diferentes — o documento do produtor tem `produtoVenda.variedadeId`, o painel
 * do comprador tem a variedade por nome. A REGRA é uma só; o que muda é de onde
 * se lê variedade, rótulo e kg.
 */
export function distribuicaoPorVariedade<T>(
  itens: readonly T[],
  acesso: {
    /** O que agrupa: variedade, nunca classificação. */
    chave: (item: T) => string;
    /** O que aparece na linha: "Prata 1ª". */
    rotulo: (item: T) => string;
    kg: (item: T) => number;
  },
): { rotulo: string; pct: number }[] {
  const porVariedade = new Map<string, T[]>();
  for (const item of itens) {
    const chave = acesso.chave(item);
    if (!porVariedade.has(chave)) porVariedade.set(chave, []);
    porVariedade.get(chave)!.push(item);
  }

  const totalDoGrupo = new Map<string, number>();
  for (const [chave, grupo] of Array.from(porVariedade.entries())) {
    if (grupo.length <= 1) continue;
    const total = grupo.reduce((acc, it) => acc + acesso.kg(it), 0);
    if (total <= 0) continue;
    totalDoGrupo.set(chave, total);
  }

  // Varre a ENTRADA de novo em vez de concatenar grupo a grupo: é o que preserva
  // a ordem da tabela mesmo quando os itens de uma variedade não estão juntos.
  const partes: { rotulo: string; pct: number }[] = [];
  for (const item of itens) {
    const total = totalDoGrupo.get(acesso.chave(item));
    if (total === undefined) continue;
    partes.push({ rotulo: acesso.rotulo(item), pct: (acesso.kg(item) / total) * 100 });
  }
  return partes;
}
